import { API_V1_BASE_URL } from '../../config/api';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_V1_BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // Keep the HTTP status fallback.
    }
    throw new Error(message);
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json() as Promise<T>;
}

export type TicketStatus = 'OPEN' | 'ACKNOWLEDGED' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | 'CANCELLED';
export type TicketPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface TicketCategory {
  id: number;
  code: string;
  name: string;
  description: string;
  autoRaised: boolean;
}

export interface TicketSla {
  responseMet: boolean;
  resolutionMet: boolean;
  isActive: boolean;
  resolutionState: 'met' | 'on_track' | 'at_risk' | 'breached';
  hoursToResolutionDue: number;
  responseDueAt: string;
  resolutionDueAt: string;
}

export interface Ticket {
  id: string;
  reference: string;
  categoryId: number;
  category: string;
  categoryCode: string;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  raisedBy: string;
  assetId: string | null;
  productOrCampaign: string | null;
  assignedTo: string | null;
  createdAt: string;
  updatedAt: string;
  responseDueAt: string;
  resolutionDueAt: string;
  firstRespondedAt: string | null;
  resolvedAt: string | null;
  sla: TicketSla;
  history?: TicketHistory[];
  comments?: TicketComment[];
}

export interface TicketHistory {
  id: string;
  from_status: TicketStatus | null;
  to_status: TicketStatus;
  note: string | null;
  changed_by: string | null;
  changed_at: string;
}

export interface TicketComment {
  id: string;
  author: string;
  body: string;
  is_internal: boolean;
  created_at: string;
}

export interface TicketStats {
  total: number;
  open: number;
  resolved: number;
  byStatus: Record<string, number>;
  byCategory: Record<string, number>;
  slaBreached: number;
  slaAtRisk: number;
}

export interface TicketFilters {
  status?: string;
  q?: string;
  raisedBy?: string;
}

export const ticketApi = {
  getCategories: () => request<TicketCategory[]>('/tickets/categories'),
  listTickets: (params: TicketFilters = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.q) query.set('q', params.q);
    if (params.raisedBy) query.set('raisedBy', params.raisedBy);
    return request<Ticket[]>(`/tickets${query.toString() ? `?${query.toString()}` : ''}`);
  },
  getStats: () => request<TicketStats>('/tickets/stats'),
  getTicket: (id: string) => request<Ticket>(`/tickets/${id}`),
  raiseTicket: (body: unknown) => request<Ticket>('/tickets', { method: 'POST', body: JSON.stringify(body) }),
  updateStatus: (id: string, body: unknown) => request<Ticket>(`/tickets/${id}/status`, { method: 'PATCH', body: JSON.stringify(body) }),
  addComment: (id: string, body: unknown) => request<TicketComment>(`/tickets/${id}/comments`, { method: 'POST', body: JSON.stringify(body) }),
};
